import { useEffect, useRef, useState } from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";
import { useT } from "@/lib/i18n/I18nProvider";

function useMetrics() {
  const { locale } = useT();
  const isBg = locale === "bg";
  return [
    {
      value: 10,
      suffix: isBg ? " млн.+" : "M+",
      label: isBg ? "проследявани публикации дневно" : "Mentions tracked daily",
    },
    { value: 25, suffix: "+", label: isBg ? "езика" : "Languages covered" },
    { value: 70, suffix: "+", label: isBg ? "партньори и клиенти" : "Brands across Europe" },
    {
      value: 99.9,
      suffix: "%",
      label: isBg ? "надеждност на платформата" : "Platform uptime",
      format: (n: number) => (isBg ? n.toFixed(1).replace(".", ",") : n.toFixed(1)),
    },
  ];
}

function CountUp({ value, suffix, format }: { value: number; suffix?: string; format?: (n: number) => string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });
  const reduce = useReducedMotion();
  // SSR and no-JS show the real value; the count-up only runs client-side once in view.
  const [display, setDisplay] = useState<string>(format ? format(value) : String(value));

  useEffect(() => {
    if (!inView || reduce) return;
    const controls = animate(0, value, {
      duration: 1.4,
      ease: [0.22, 0.8, 0.2, 1],
      onUpdate: (latest) => {
        setDisplay(format ? format(latest) : String(Math.round(latest)));
      },
    });
    return () => controls.stop();
  }, [inView, value, format, reduce]);

  return (
    <span ref={ref} className="text-gradient-brand font-display text-[clamp(2.5rem,4.4vw,3.75rem)] font-medium tabular-nums leading-none">
      {display}
      {suffix}
    </span>
  );
}

export function MetricsStrip() {
  const METRICS = useMetrics();
  return (
    <section className="relative border-y border-border/60 bg-background py-16 lg:py-20">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <ul className="grid grid-cols-2 gap-y-10 lg:grid-cols-4 lg:gap-0">
          {METRICS.map((m, i) => (
            <li
              key={m.label}
              className={
                "flex flex-col items-center gap-3 px-4 text-center" +
                (i > 0 ? " lg:border-l lg:border-border/60" : "")
              }
            >
              <CountUp value={m.value} suffix={m.suffix} format={m.format} />
              <p className="text-[12.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                {m.label}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}